import { FeatureFlags } from "@hooks/useQueryFeatureFlags";
import { BENEFITS, Benefit, BenefitGate } from "../data";
import { BenefitsSection, BenefitsTab } from "../data/sections";
import { selectorBenefitIsGateOpen } from "./selectorBenefitIsGateOpen";
import { selectorSortedBenefits } from "./selectorSortedBenefits";

/**
 * Gates that describe availability rather than section membership. A benefit
 * carrying one of these still belongs to the ordinary catalog; it is just
 * hidden until the gate opens.
 */
const AVAILABILITY_GATES: BenefitGate[] = [
  BenefitGate.MeridioFlag,
  BenefitGate.MasterPolicy
];

const belongsToSection = (benefit: Benefit, section: BenefitsSection) =>
  section.gate
    ? benefit.gate === section.gate
    : !benefit.gate || AVAILABILITY_GATES.includes(benefit.gate);

/** The benefits to render for a section, optionally narrowed to one of its tabs. */
export const selectorCatalogBenefits = (params: {
  section: BenefitsSection;
  tab?: BenefitsTab;
  featureFlags?: FeatureFlags;
  showMasterPolicy: boolean;
  benefits?: Benefit[];
}): Benefit[] => {
  const {
    section,
    tab,
    featureFlags,
    showMasterPolicy,
    benefits = BENEFITS
  } = params;

  const visible = benefits.filter(benefit => {
    if (!belongsToSection(benefit, section)) {
      return false;
    }

    if (
      !selectorBenefitIsGateOpen({ benefit, featureFlags, showMasterPolicy })
    ) {
      return false;
    }

    if (section.categories && !section.categories.includes(benefit.category)) {
      return false;
    }

    // Upsell sections list only what is flagged as additional coverage.
    // Every other section lists the whole catalog for its categories.
    if (section.isAdditional && !benefit.isAdditionalCoverage) {
      return false;
    }

    return tab?.match ? tab.match(benefit) : true;
  });

  return selectorSortedBenefits(visible);
};
